import { alpha } from "@mui/material/styles";

import { type ComponentOverrides } from "theme/theme.types";

import { RADIO_DOT_CLASS, RADIO_GLYPH, RADIO_GLYPH_CLASS } from "theme/components/radio-glyphs";

/** One size only — Figma draws none other — so `small` and `medium` render the same 16px glyph. */
const RADIO_GLYPH_SIZE = 16;

/**
 * Figma's `radio/p`, the ring the halo is painted in. Negated as a margin, as on the checkbox, so it
 * costs no layout; MUI's input fills the padded root, so it doubles as a 32px pointer target.
 */
const RADIO_AFFORDANCE = 8;

/** Figma's `radio/focusRipple/focused` and `/pressed`, laid over the halo in `primary.light`. */
const RADIO_FOCUSED_OPACITY = 0.16;
const RADIO_PRESSED_OPACITY = 0.2;

/**
 * Figma `Radio group/Form control label` (node `4057:28194`, page `23745:11379`).
 *
 * Same division of labour as `MuiCheckbox`: the halo is the root's `background-color`, the glyph lives
 * on `.ApolloRadio-glyph`, and the glyph's `color` is set on the glyph itself so the root's stays free
 * to tint MUI's ripple. The ripple and MUI's own `alpha(…, hoverOpacity)` hover tint are kept; the
 * halo rules out-paint that tint on source order, because theme overrides serialize after the
 * component's variants. Hover is `primary.lighter`; focus and press are Figma's `focusRipple` layer,
 * `primary.light` at 16% / 20%, written after hover so a pressed pointer shows the press.
 *
 * MUI's check animation is kept, which is what the callback form of `root` is for — and why both icon
 * slots get the same element (`radio-glyphs.tsx`). Never transition `all`: `background-color` is the
 * halo, and animating it would smear every hover.
 */
export const MuiRadio: ComponentOverrides["MuiRadio"] = {
  defaultProps: {
    icon: RADIO_GLYPH,
    checkedIcon: RADIO_GLYPH,
  },
  styleOverrides: {
    root: ({ theme }) => ({
      padding: RADIO_AFFORDANCE,
      margin: -RADIO_AFFORDANCE,

      // Before `:active` and the focus block: on equal specificity they must out-paint the touch reset.
      "&:hover": {
        backgroundColor: theme.palette.primary.lighter,
        // A tap leaves `:hover` stuck on touch screens; `:active` still answers the press.
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
      "&.Mui-focusVisible": {
        backgroundColor: alpha(theme.palette.primary.light, RADIO_FOCUSED_OPACITY),
        [`& .${RADIO_GLYPH_CLASS}`]: {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      },
      "&:active": {
        backgroundColor: alpha(theme.palette.primary.light, RADIO_PRESSED_OPACITY),
      },

      [`& .${RADIO_GLYPH_CLASS}`]: {
        display: "block",
        width: RADIO_GLYPH_SIZE,
        height: RADIO_GLYPH_SIZE,
        borderRadius: "50%",
        color: theme.palette.common.black,
        // White in every state, disabled included — unlike the checkbox's grey disabled box, as Figma draws it.
        backgroundColor: theme.palette.background.paper,
      },
      [`& .${RADIO_DOT_CLASS}`]: {
        transformBox: "fill-box",
        transformOrigin: "center",
        transform: "scale(0)",
        transition: theme.transitions.create("transform", {
          easing: theme.transitions.easing.easeIn,
          duration: theme.transitions.duration.shortest,
        }),
      },
      "&.Mui-checked": {
        [`& .${RADIO_GLYPH_CLASS}`]: {
          color: theme.palette.primary.main,
        },
        [`& .${RADIO_DOT_CLASS}`]: {
          transform: "scale(1)",
          transition: theme.transitions.create("transform", {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shortest,
          }),
        },
      },
      "&.Mui-disabled": {
        [`& .${RADIO_GLYPH_CLASS}`]: {
          color: theme.palette.text.disabled,
        },
      },
    }),
  },
};
